import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ChatStore } from '@application/chat.store';
import { Message } from '@domain/models';

@Component({
  selector: 'app-payment-result-manual-review',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './payment-result-manual-review.component.html',
  styleUrl: './payment-result-manual-review.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PaymentManualReviewResultComponent {
  private readonly chatStore = inject(ChatStore);
  readonly message = input.required<Message>();
  readonly messageText = computed(() => {
    const text: unknown = this.message().text;
    return typeof text === 'string' ? text : '';
  });
  readonly formMode = input(false);
  readonly formRequested = output<Message>();
  readonly requestSent = signal(false);
  readonly operationDate = signal('');
  readonly amount = signal('');
  readonly operationNumber = signal('');
  readonly submitting = signal(false);
  readonly submitted = signal(false);
  readonly submitError = signal(false);
  readonly isAmountValid = computed(() => {
    const amountText = this.amount();
    const amount = Number(amountText);
    return !!amountText
      && /^\d{1,6}(?:\.\d{1,2})?$/.test(amountText)
      && Number.isFinite(amount)
      && amount >= 0
      && amount <= 999999.99;
  });
  readonly isOperationNumberValid = computed(() => /^\d{8}$/.test(this.operationNumber()));
  readonly canSubmit = computed(() => {
    return !this.submitting()
      && !this.submitted()
      && !!this.operationDate()
      && this.isAmountValid()
      && this.isOperationNumberValid();
  });

  requestManualReviewForm(): void {
    if (this.message().paymentRetryStatus !== 'AVAILABLE') return;
    this.requestSent.set(true);
    this.formRequested.emit(this.message());
  }

  onOperationNumberChange(value: string): void {
    this.operationNumber.set(value.replace(/\D/g, '').slice(0, 8));
  }

  onOperationNumberBeforeInput(event: InputEvent): void {
    this.preventInvalidInsertion(event, /^\d{0,8}$/);
  }

  onOperationNumberPaste(event: ClipboardEvent): void {
    this.preventInvalidPaste(event, /^\d{0,8}$/);
  }

  onAmountChange(value: string): void {
    const sanitized = value.replace(/[^\d.]/g, '');
    const decimalIndex = sanitized.indexOf('.');
    if (decimalIndex === -1) {
      this.amount.set(sanitized.slice(0, 6));
      return;
    }

    const integerPart = sanitized.slice(0, decimalIndex).slice(0, 6) || '0';
    const decimalPart = sanitized.slice(decimalIndex + 1).replace(/\./g, '').slice(0, 2);
    this.amount.set(`${integerPart}.${decimalPart}`);
  }

  onAmountBeforeInput(event: InputEvent): void {
    this.preventInvalidInsertion(event, /^\d{0,6}(?:\.\d{0,2})?$/);
  }

  onAmountPaste(event: ClipboardEvent): void {
    this.preventInvalidPaste(event, /^\d{0,6}(?:\.\d{0,2})?$/);
  }

  private preventInvalidInsertion(event: InputEvent, pattern: RegExp): void {
    const inserted = event.data ?? event.dataTransfer?.getData('text/plain');
    if (inserted == null) return;
    const input = event.target as HTMLInputElement;
    if (!this.matchesInsertion(input, inserted, pattern)) event.preventDefault();
  }

  private preventInvalidPaste(event: ClipboardEvent, pattern: RegExp): void {
    const inserted = event.clipboardData?.getData('text/plain');
    if (inserted == null) return;
    const input = event.target as HTMLInputElement;
    if (!this.matchesInsertion(input, inserted, pattern)) event.preventDefault();
  }

  private matchesInsertion(input: HTMLInputElement, inserted: string, pattern: RegExp): boolean {
    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? start;
    const value = `${input.value.slice(0, start)}${inserted}${input.value.slice(end)}`;
    return pattern.test(value);
  }

  async submit(): Promise<void> {
    if (this.message().paymentRetryStatus !== 'AVAILABLE' || !this.canSubmit()) return;
    const amount = Number(this.amount());
    this.submitting.set(true);
    this.submitError.set(false);
    const message = this.message();
    const result = await this.chatStore.submitPaymentRetry(
      message.messageId ?? message.id,
      {
        paymentDate: this.operationDate(),
        paymentAmount: amount,
        paymentOperationNumber: this.operationNumber()
      }
    );
    this.submitting.set(false);
    if (result.ok) {
      this.submitted.set(true);
    } else {
      this.submitError.set(true);
    }
  }
}
